import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { breakpoints } from './breakpoints';
import { palette } from './palette';
import { shadows } from './shadows';
import { overrides } from './overrides';
import { getTypography } from './typography';
import { customShadows } from './custom-shadows';
import { ALIGNMENT_CONSTANTS, THEME_MODE } from './contants';

export const themeContext = React.createContext({
    themeMode: '',
    themeModeHandler: () => {},
    fontSize: 14,
    fontSizeHandler: () => {},
    fontFamilyIndex: 0,
    fontFamilyIndexHandler: () => {},
    isContrast: false,
    isContrastHandler: () => {},
    preset: '',
    presetHandler: () => {},
    resetHandler: null,
    alignment: '',
    alignmentHandler: null,
});
export default function ThemeProvider({ children }) {
    const [fontFamilyIndex, setFontFamilyIndex] = useState(0);
    const [themeMode, setThemeMode] = useState(THEME_MODE.light);
    const [isContrast, setIsContrast] = useState(false);
    const [alignment, setAlignment] = useState(ALIGNMENT_CONSTANTS.left);
    const [fontSize, setFontSize] = useState(16);
    const [preset, setPreset] = useState(1);

    const presetHandler = (value) => setPreset(value);
    const fontSizeHandler = (value) => setFontSize(value);
    const isContrastHandler = (value) => setIsContrast((prevState) => !prevState);

    const themeModeHandler = () =>
        setThemeMode((prevState) =>
            prevState === THEME_MODE.light ? THEME_MODE.dark : THEME_MODE.light
        );

    const alignmentHandler = (value) => setAlignment(value);

    const resetHandler = () => {
        setFontFamilyIndex(0);
        setThemeMode(THEME_MODE.light);
        setIsContrast(false);
        setAlignment(ALIGNMENT_CONSTANTS.left);
        setFontSize(16);
        setPreset(1);
    };

    const fontFamilyIndexHandler = (index) => setFontFamilyIndex(index);

    const memoizedValue = useMemo(
        () => ({
            breakpoints,
            palette: palette(themeMode, isContrast, preset),
            typography: getTypography({ cFontFamilyIndex: fontFamilyIndex }),
            shadows: shadows(themeMode),
            customShadows: customShadows(themeMode),
            shape: { borderRadius: 8 },
        }),
        [themeMode, fontFamilyIndex, isContrast, preset]
    );
    const theme = createTheme(memoizedValue);
    theme.components = overrides(theme);
    // const theme = createTheme({});

    useEffect(() => {
        // set fontSize
        // document.documentElement.style.fontSize = `${fontSize}px`; // Sets the HTML font size to 16px
        document.documentElement.style.setProperty('--font-size', `${fontSize}px`); // Sets the HTML font size to 16px
    }, [fontSize]);

    return (
        <themeContext.Provider
            value={{
                fontSize,
                fontSizeHandler,
                themeMode,
                themeModeHandler,
                fontFamilyIndex,
                fontFamilyIndexHandler,
                preset,
                presetHandler,
                isContrast,
                isContrastHandler,
                alignment,
                alignmentHandler,
                resetHandler,
            }}
        >
            <MUIThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MUIThemeProvider>
        </themeContext.Provider>
    );
}

ThemeProvider.propTypes = {
    children: PropTypes.node,
};
